import React from "react";
import {
  X,
  History,
  Calendar,
  Activity,
  Check,
  Clock,
  FileText,
  ArrowRight,
  ArrowDownRight,
} from "lucide-react";

const EmployeeHistoryModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  employee,
  historyData,
  getEmployeeName,
  ACTION_TYPES,
  onClose,
}) => {
  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(95vw, 900px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #06b6d4, #0891b2)",
              }}
            >
              <History size={26} color="white" />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.employeeHistoryTitle}</h2>
              <p
                style={{
                  fontSize: "clamp(12px, 1.7vw, 14px)",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                  margin: "4px 0 0 0",
                  fontWeight: "600",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {getEmployeeName(employee)} - {employee.clientCode || employee.infinityCode}
              </p>
            </div>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        {/* Current Employee Info */}
        <div style={styles.infoBox}>
          <h3
            style={{
              fontSize: "clamp(15px, 2.5vw, 17px)",
              fontWeight: "800",
              marginBottom: "14px",
              color: theme === "dark" ? "#ffffff" : "#0f172a",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t.currentInfo}
          </h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.currentPosition}</span>
            <span style={styles.infoValue}>
              {employee.position ?? employee.employeeType ?? t.notAvailable}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.currentDepartment}</span>
            <span style={styles.infoValue}>
              {employee.department?.name ?? employee.employeeType ?? t.notAvailable}
            </span>
          </div>
          <div
            style={{
              ...styles.infoRow,
              borderBottom: "none",
              marginBottom: 0,
              paddingBottom: 0,
            }}
          >
            <span style={styles.infoLabel}>{t.currentSalary}</span>
            <span style={styles.infoValue}>
              {employee.netSalary
                ? `${t.egp} ${employee.netSalary.toLocaleString()}`
                : t.notAvailable}
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div style={{ marginTop: "24px" }}>
          <h3
            style={{
              fontSize: "clamp(16px, 2.8vw, 18px)",
              fontWeight: "800",
              marginBottom: "20px",
              color: theme === "dark" ? "#ffffff" : "#0f172a",
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Activity size={20} />
            {t.actionTimeline} ({historyData.length} {t.records})
          </h3>

          {historyData.length > 0 ? (
            <div
              style={{
                position: "relative",
                [isRTL ? "paddingRight" : "paddingLeft"]: "40px",
              }}
            >
              {/* Timeline Line */}
              <div
                style={{
                  position: "absolute",
                  [isRTL ? "right" : "left"]: "16px",
                  top: "20px",
                  bottom: "20px",
                  width: "3px",
                  background:
                    theme === "dark"
                      ? "linear-gradient(180deg, #2e7cba, #06b6d4)"
                      : "linear-gradient(180deg, #60a5fa, #22d3ee)",
                  borderRadius: "10px",
                }}
              />

              {/* Timeline Items */}
              {historyData.map((action, index) => {
                const actionConfig = ACTION_TYPES.find(
                  (a) => a.id === action.actionType
                );
                const ActionIcon = actionConfig?.icon || FileText;
                const actionDate = new Date(
                  action.effectiveDate || action.createdAt
                );

                return (
                  <div
                    key={action.id}
                    style={{
                      position: "relative",
                      marginBottom:
                        index === historyData.length - 1 ? "0" : "24px",
                    }}
                  >
                    {/* Timeline Dot */}
                    <div
                      style={{
                        position: "absolute",
                        [isRTL ? "right" : "left"]: "-32px",
                        top: "8px",
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: actionConfig?.color || "#2e7cba",
                        boxShadow: `0 0 0 4px ${
                          theme === "dark"
                            ? "rgba(30, 41, 59, 1)"
                            : "rgba(255, 255, 255, 1)"
                        }`,
                        zIndex: 1,
                      }}
                    />

                    {/* Timeline Card */}
                    <div
                      style={{
                        background:
                          theme === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(255,255,255,0.9)",
                        padding: "16px",
                        borderRadius: "12px",
                        border:
                          theme === "dark"
                            ? "1px solid rgba(255,255,255,0.12)"
                            : "1px solid rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                      }}
                      className="timeline-card-hover"
                    >
                      {/* Date Header */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isRTL ? "row" : "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.75)"
                                : "#64748b",
                            fontSize: "clamp(11px, 1.6vw, 13px)",
                            fontWeight: "700",
                          }}
                        >
                          <Calendar size={14} />
                          {actionDate.toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US",
                            { year: "numeric", month: "short", day: "numeric" }
                          )}
                        </div>

                        {/* Status Badge */}
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? "rgba(16, 185, 129, 0.15)"
                                : action.status === "pending"
                                ? "rgba(245, 158, 11, 0.15)"
                                : "rgba(239, 68, 68, 0.15)",
                            border:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? "1px solid rgba(16, 185, 129, 0.4)"
                                : action.status === "pending"
                                ? "1px solid rgba(245, 158, 11, 0.4)"
                                : "1px solid rgba(239, 68, 68, 0.4)",
                            color:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? theme === "dark"
                                  ? "#34d399"
                                  : "#059669"
                                : action.status === "pending"
                                ? theme === "dark"
                                  ? "#fbbf24"
                                  : "#d97706"
                                : theme === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {action.status === "approved" ||
                          action.status === "completed" ? (
                            <Check size={12} />
                          ) : action.status === "pending" ? (
                            <Clock size={12} />
                          ) : (
                            <X size={12} />
                          )}
                          {t[action.status] || action.status}
                        </div>
                      </div>

                      {/* Action Type */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isRTL ? "row" : "row",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              actionConfig?.bgColor ||
                              "rgba(100, 116, 139, 0.15)",
                            border: `1px solid ${
                              actionConfig?.borderColor ||
                              "rgba(100, 116, 139, 0.4)"
                            }`,
                            color: theme === "dark" ? "#ffffff" : "#0f172a",
                          }}
                        >
                          <ActionIcon size={14} />
                          {actionConfig
                            ? t[actionConfig.labelKey]
                            : action.actionType}
                        </div>
                      </div>

                      {/* Changes Grid */}
                      {(action.oldPosition ||
                        action.newPosition ||
                        action.oldSalary ||
                        action.newSalary ||
                        action.oldDepartment ||
                        action.newDepartment) && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: "12px",
                            marginTop: "12px",
                            padding: "12px",
                            background:
                              theme === "dark"
                                ? "rgba(0,0,0,0.2)"
                                : "rgba(0,0,0,0.03)",
                            borderRadius: "8px",
                          }}
                        >
                          {/* Position Change */}
                          {(action.oldPosition || action.newPosition) && (
                            <div
                              style={{ textAlign: isRTL ? "right" : "left" }}
                            >
                              <div
                                style={{
                                  fontSize: "clamp(10px, 1.5vw, 11px)",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.5)"
                                      : "#94a3b8",
                                  marginBottom: "4px",
                                  fontWeight: "700",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {t.position}
                              </div>
                              <div
                                style={{
                                  fontSize: "clamp(12px, 1.7vw, 13px)",
                                  fontWeight: "600",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.65)"
                                      : "#64748b",
                                }}
                              >
                                {action.oldPosition || t.notAvailable}
                              </div>
                              {action.newPosition &&
                                action.newPosition !== action.oldPosition && (
                                  <>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isRTL
                                          ? "flex-end"
                                          : "flex-start",
                                        margin: "4px 0",
                                      }}
                                    >
                                      <ArrowDownRight
                                        size={14}
                                        style={{
                                          color:
                                            action.actionType === "promote"
                                              ? "#10b981"
                                              : "#ef4444",
                                          transform: isRTL
                                            ? "rotate(180deg)"
                                            : "none",
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "clamp(12px, 1.7vw, 13px)",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#ffffff"
                                            : "#0f172a",
                                      }}
                                    >
                                      {action.newPosition}
                                    </div>
                                  </>
                                )}
                            </div>
                          )}

                          {/* Salary Change */}
                          {(action.oldSalary || action.newSalary) && (
                            <div
                              style={{ textAlign: isRTL ? "right" : "left" }}
                            >
                              <div
                                style={{
                                  fontSize: "clamp(10px, 1.5vw, 11px)",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.5)"
                                      : "#94a3b8",
                                  marginBottom: "4px",
                                  fontWeight: "700",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {t.salary}
                              </div>
                              <div
                                style={{
                                  fontSize: "clamp(12px, 1.7vw, 13px)",
                                  fontWeight: "600",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.65)"
                                      : "#64748b",
                                }}
                              >
                                {t.egp}{" "}
                                {(action.oldSalary || 0).toLocaleString()}
                              </div>
                              {action.newSalary &&
                                action.newSalary !== action.oldSalary && (
                                  <>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isRTL
                                          ? "flex-end"
                                          : "flex-start",
                                        margin: "4px 0",
                                      }}
                                    >
                                      <ArrowDownRight
                                        size={14}
                                        style={{
                                          color:
                                            action.newSalary > action.oldSalary
                                              ? "#10b981"
                                              : "#ef4444",
                                          transform: isRTL
                                            ? "rotate(180deg)"
                                            : "none",
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "clamp(12px, 1.7vw, 13px)",
                                        fontWeight: "700",
                                        color:
                                          action.newSalary > action.oldSalary
                                            ? "#10b981"
                                            : "#ef4444",
                                      }}
                                    >
                                      {t.egp}{" "}
                                      {action.newSalary.toLocaleString()}
                                      <span
                                        style={{
                                          fontSize: "clamp(10px, 1.5vw, 11px)",
                                          [isRTL
                                            ? "marginRight"
                                            : "marginLeft"]: "6px",
                                        }}
                                      >
                                        (
                                        {action.newSalary > action.oldSalary
                                          ? "+"
                                          : ""}
                                        {action.changePercent?.toFixed(1)}%)
                                      </span>
                                    </div>
                                  </>
                                )}
                            </div>
                          )}

                          {/* Department Change */}
                          {(action.oldDepartment || action.newDepartment) && (
                            <div
                              style={{ textAlign: isRTL ? "right" : "left" }}
                            >
                              <div
                                style={{
                                  fontSize: "clamp(10px, 1.5vw, 11px)",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.5)"
                                      : "#94a3b8",
                                  marginBottom: "4px",
                                  fontWeight: "700",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {t.department}
                              </div>
                              <div
                                style={{
                                  fontSize: "clamp(12px, 1.7vw, 13px)",
                                  fontWeight: "600",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.65)"
                                      : "#64748b",
                                }}
                              >
                                {action.oldDepartment || t.notAvailable}
                              </div>
                              {action.newDepartment &&
                                action.newDepartment !==
                                  action.oldDepartment && (
                                  <>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isRTL
                                          ? "flex-end"
                                          : "flex-start",
                                        margin: "4px 0",
                                      }}
                                    >
                                      <ArrowRight
                                        size={14}
                                        style={{
                                          color: "#2e7cba",
                                          transform: isRTL
                                            ? "rotate(180deg)"
                                            : "none",
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "clamp(12px, 1.7vw, 13px)",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#ffffff"
                                            : "#0f172a",
                                      }}
                                    >
                                      {action.newDepartment}
                                    </div>
                                  </>
                                )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Reason */}
                      {action.reason && (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "10px",
                            background:
                              theme === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(0,0,0,0.02)",
                            borderRadius: "6px",
                            [isRTL
                              ? "borderRight"
                              : "borderLeft"]: `3px solid ${
                              actionConfig?.color || "#2e7cba"
                            }`,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "clamp(11px, 1.6vw, 12px)",
                              color:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.75)"
                                  : "#64748b",
                              lineHeight: "1.5",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {action.reason}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <History size={60} />
              </div>
              <h3 style={styles.emptyStateTitle}>{t.noHistoryFound}</h3>
              <p style={styles.emptyStateText}>{t.noActionsForEmployee}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeHistoryModal;